import { WeatherIcons } from "../models/ui/weatherForecast";

/**
 * WMO (World Meteorological Organization) weather codes to icon mapping.
 * Backend sends WMO codes from Open-Meteo (direct) and WeatherAPI (via weatherApiCodeToWmo).
 * @see https://open-meteo.com/en/docs - WMO Weather interpretation codes (WW)
 */
export const CONDITION_CODES_TO_WEATHER_ICONS_MAP: WeatherIcons = {
  // 0: Clear sky
  0: {
    day: { icon: "clear-day", alt: "Clear day" },
    night: { icon: "clear-night", alt: "Clear night" },
  },
  // 1: Mainly clear
  1: {
    day: { icon: "partly-cloudy-day", alt: "Mainly clear" },
    night: { icon: "partly-cloudy-night", alt: "Mainly clear" },
  },
  // 2: Partly cloudy
  2: {
    day: { icon: "partly-cloudy-day", alt: "Partly cloudy day" },
    night: { icon: "partly-cloudy-night", alt: "Partly cloudy night" },
  },
  // 3: Overcast
  3: {
    day: { icon: "overcast", alt: "Overcast" },
    night: { icon: "overcast", alt: "Overcast" },
  },
  // 45: Fog
  45: {
    day: { icon: "mist", alt: "Mist" },
    night: { icon: "mist", alt: "Mist" },
  },
  // 48: Depositing rime fog
  48: {
    day: { icon: "fog", alt: "Fog" },
    night: { icon: "fog", alt: "Fog" },
  },
  // 51, 53, 55: Drizzle (light, moderate, dense)
  51: {
    day: { icon: "drizzle", alt: "Drizzle" },
    night: { icon: "drizzle", alt: "Drizzle" },
  },
  53: {
    day: { icon: "drizzle", alt: "Drizzle" },
    night: { icon: "drizzle", alt: "Drizzle" },
  },
  55: {
    day: { icon: "drizzle", alt: "Drizzle" },
    night: { icon: "drizzle", alt: "Drizzle" },
  },
  // 56, 57: Freezing drizzle
  56: {
    day: { icon: "partly-cloudy-day-drizzle", alt: "Freezing drizzle" },
    night: { icon: "partly-cloudy-night-drizzle", alt: "Freezing drizzle" },
  },
  57: {
    day: { icon: "drizzle", alt: "Freezing drizzle" },
    night: { icon: "drizzle", alt: "Freezing drizzle" },
  },
  // 61, 63, 65: Rain (slight, moderate, heavy)
  61: {
    day: { icon: "rain", alt: "Rain" },
    night: { icon: "rain", alt: "Rain" },
  },
  63: {
    day: { icon: "rain", alt: "Rain" },
    night: { icon: "rain", alt: "Rain" },
  },
  65: {
    day: { icon: "rain", alt: "Rain" },
    night: { icon: "rain", alt: "Rain" },
  },
  // 66, 67: Freezing rain (sleet)
  66: {
    day: { icon: "sleet", alt: "Sleet" },
    night: { icon: "sleet", alt: "Sleet" },
  },
  67: {
    day: { icon: "sleet", alt: "Sleet" },
    night: { icon: "sleet", alt: "Sleet" },
  },
  // 71, 73, 75: Snow
  71: {
    day: { icon: "snow", alt: "Snow" },
    night: { icon: "snow", alt: "Snow" },
  },
  73: {
    day: { icon: "snow", alt: "Snow" },
    night: { icon: "snow", alt: "Snow" },
  },
  75: {
    day: { icon: "snow", alt: "Snow" },
    night: { icon: "snow", alt: "Snow" },
  },
  // 77: Snow grains
  77: {
    day: { icon: "hail", alt: "Snow grains" },
    night: { icon: "hail", alt: "Snow grains" },
  },
  // 80, 81, 82: Rain showers
  80: {
    day: { icon: "rain", alt: "Rain showers" },
    night: { icon: "rain", alt: "Rain showers" },
  },
  81: {
    day: { icon: "rain", alt: "Rain showers" },
    night: { icon: "rain", alt: "Rain showers" },
  },
  82: {
    day: { icon: "rain", alt: "Rain showers" },
    night: { icon: "rain", alt: "Rain showers" },
  },
  // 85, 86: Snow showers
  85: {
    day: { icon: "snow", alt: "Snow showers" },
    night: { icon: "snow", alt: "Snow showers" },
  },
  86: {
    day: { icon: "snow", alt: "Snow showers" },
    night: { icon: "snow", alt: "Snow showers" },
  },
  // 95: Thunderstorm
  95: {
    day: { icon: "thunderstorms", alt: "Thunderstorms" },
    night: { icon: "thunderstorms", alt: "Thunderstorms" },
  },
  // 96, 99: Thunderstorm with hail
  96: {
    day: { icon: "thunderstorms-rain", alt: "Thunderstorm with hail" },
    night: { icon: "thunderstorms-rain", alt: "Thunderstorm with hail" },
  },
  99: {
    day: { icon: "thunderstorms-rain", alt: "Thunderstorm with hail" },
    night: { icon: "thunderstorms-rain", alt: "Thunderstorm with hail" },
  },
};

/** Default icon for unknown or unmapped WMO codes */
const DEFAULT_WMO_ICON = {
  day: { icon: "cloudy", alt: "Weather" },
  night: { icon: "cloudy", alt: "Weather" },
} as const;

/**
 * Resolves a WMO condition code to the icon mapping.
 * Returns default icon for unknown codes to prevent runtime errors.
 */
export const getWeatherIconForWmoCode = (conditionCode: number | null | undefined) => {
  if (conditionCode === null || conditionCode === undefined) {
    return DEFAULT_WMO_ICON;
  }
  return CONDITION_CODES_TO_WEATHER_ICONS_MAP[conditionCode] ?? DEFAULT_WMO_ICON;
};
